import React, { useMemo, useState, useCallback } from "react";
import images from "../../../images";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";

const SliderQuestion = ({
  isRequired,
  setIsRequired,
  options: initialOptions,
  setOptions,
  isAdditionalComments,
  setIsAdditionalComments,
  commentPlaceholder,
  setCommentPlaceholder,
  isCompulsory,
  setIsCompulsory,
}) => {
  const { t } = useTranslation();
  const [displayMaxValue, setDisplayMaxValue] = useState(
    Math.min(Math.max(initialOptions.length, 2), 10) || 5
  );

  const normalizedOptions = useMemo(() => {
    return initialOptions
      .map((opt, index) => ({
        id: opt.id || `${index + 1}`,
        option_text:
          opt.option_text ?? t("base.optionLabel", { number: index + 1 }),
        value: opt.value || `${index + 1}`,
        checked: opt.checked || false,
      }))
      .sort((a, b) => {
        const valueA = Number(a.value);
        const valueB = Number(b.value);

        if (isNaN(valueA) && isNaN(valueB)) return 0;
        if (isNaN(valueA)) return 1;
        if (isNaN(valueB)) return -1;

        return valueA - valueB;
      });
  }, [initialOptions, t]);

  const minValue = 1;

  const handleMaxValueChange = useCallback(
    (e) => {
      const value = e.target.value;
      const parsedValue = Number(value);

      if (isNaN(parsedValue) || parsedValue < 2 || parsedValue > 10) {
        return;
      }

      setOptions((prevOptions) => {
        const newOptions = [...prevOptions];
        if (parsedValue > prevOptions.length) {
          for (let i = prevOptions.length; i < parsedValue; i++) {
            newOptions.push({
              id: `${i + 1}`,
              option_text: t("base.optionLabel", { number: i + 1 }),
              value: `${i + 1}`,
              checked: false,
            });
          }
        }
        return newOptions;
      });

      setDisplayMaxValue(parsedValue);
    },
    [setOptions, t]
  );

  const handleOptionTextChange = useCallback(
    (index, newText) => {
      setOptions((prevOptions) => {
        const newOptions = [...prevOptions];
        if (index >= 0 && index < newOptions.length) {
          newOptions[index] = {
            ...newOptions[index],
            option_text: newText || "",
          };
        }
        return newOptions;
      });
    },
    [setOptions]
  );

  const debounceHandleSetCommentPlaceholder = useMemo(
    () => debounce((value) => setCommentPlaceholder(value), 50),
    [setCommentPlaceholder]
  );

  const handleCommentPlaceholder = (e) => {
    const value = e.target.value;
    debounceHandleSetCommentPlaceholder(value);
  };

  const maxValueOptions = useMemo(() => {
    return Array.from({ length: 9 }, (_, i) => i + 2).map((i) => (
      <option key={i} value={i}>
        {i}
      </option>
    ));
  }, []);

  return (
    <>
      <div className="space-y-4 mb-4">
        <div className="flex justify-between gap-4">
          <div className="bg-white rounded-full p-3 flex items-center justify-between w-1/2">
            <span>{minValue}</span>
          </div>
          <div className="bg-white rounded-full p-3 flex items-center justify-between w-1/2">
            <select
              value={displayMaxValue}
              onChange={handleMaxValueChange}
              className="w-full border-0 focus:outline-none focus:ring-0 focus:border-transparent bg-transparent text-center appearance-none"
              aria-label={t("base.selectMaxValue")}
            >
              {maxValueOptions}
            </select>
            <img src={images.ChevronDownIcon} alt="" className="w-4 h-4" />
          </div>
        </div>

        <div className="space-y-3">
          {Array.from({ length: displayMaxValue }, (_, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="text-gray-500 w-6 text-center"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <div className="bg-white rounded-full p-4 flex-1">
                <input
                  type="text"
                  value={normalizedOptions[index]?.option_text || ""}
                  onChange={(e) =>
                    handleOptionTextChange(index, e.target.value)
                  }
                  className="w-full border-0 focus:outline-none focus:ring-0 focus:border-transparent bg-transparent"
                  placeholder={t("base.optionLabel", { number: index + 1 })}
                  aria-label={t("base.optionLabel", { number: index + 1 })}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4 mb-4">
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isRequired}
              onChange={() => setIsRequired(!isRequired)}
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
          <span className="text-gray-500">{t("base.required")}</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isAdditionalComments}
              onChange={() => setIsAdditionalComments(!isAdditionalComments)}
            />
            <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
          </label>
          <span className="text-gray-500">
            {t("base.addAdditionalComments")}
          </span>
        </div>

        {isAdditionalComments && (
          <>
            <div className="bg-white rounded-lg p-4">
              <input
                type="text"
                value={commentPlaceholder}
                onChange={handleCommentPlaceholder}
                className="w-full border-0 focus:ring-0 text-sm text-gray-400 focus:outline-none"
                placeholder={t("base.pleaseElaborate")}
              />
            </div>
            <div className="flex justify-between">
              <div></div>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isCompulsory}
                    onChange={() => setIsCompulsory(!isCompulsory)}
                  />
                  <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
                <span className="text-gray-500">{t("base.compulsory")}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default SliderQuestion;
